import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    CreditCard, TrendingUp, Clock, CheckCircle2, AlertTriangle, Calendar, Flame, Loader2,
    Banknote, X,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAppContext } from '../../context/useAppContext';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { useLandlordPlan } from '../../lib/useLandlordPlan';

// ────────────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────────────

interface RentPayment {
    id: string;
    property_id: string;
    tenant_link_id: string | null;
    amount: number;
    due_date: string | null;
    paid_date: string | null;
    status: 'paid' | 'pending' | 'overdue';
    period_month: string | null;
    days_late: number;
    notes: string | null;
}

interface LinkRow {
    id: string;
    property_id: string;
    tenant_user_id: string | null;
    properties: { name: string; rent_amount: number | null } | null;
}

// ────────────────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────────────────

export default function TenantPayments() {
    const { user } = useAppContext();
    const [searchParams, setSearchParams] = useSearchParams();
    const landlord = useLandlordPlan();
    const [payments, setPayments] = useState<RentPayment[]>([]);
    const [link, setLink] = useState<LinkRow | null>(null);
    const [loading, setLoading] = useState(true);
    const [payingId, setPayingId] = useState<string | null>(null);
    const paidFlag = searchParams.get('paid') === 'true';
    const canceledFlag = searchParams.get('canceled') === 'true';

    useEffect(() => {
        if (!user.id) return;
        loadData();
    }, [user.id]);

    const dismissFlag = () => {
        searchParams.delete('paid');
        searchParams.delete('canceled');
        setSearchParams(searchParams, { replace: true });
    };

    const handlePayNow = async (payment: RentPayment) => {
        if (!user.id) return;
        setPayingId(payment.id);
        try {
            // Server identifies the tenant from the Bearer token, not the body.
            const { data: sessionData } = await supabase.auth.getSession();
            const accessToken = sessionData?.session?.access_token;
            const headers: Record<string, string> = { 'Content-Type': 'application/json' };
            if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

            const res = await fetch('/api/create-rent-payment-session', {
                method: 'POST',
                headers,
                body: JSON.stringify({ rentPaymentId: payment.id }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to start checkout');
            window.location.href = data.url;
        } catch (err: any) {
            alert('Could not start payment: ' + err.message);
            setPayingId(null);
        }
    };

    const loadData = async () => {
        setLoading(true);
        try {
            const { data: linkRow } = await supabase
                .from('tenant_links')
                .select('id, property_id, tenant_user_id, properties(name, rent_amount)')
                .eq('tenant_user_id', user.id)
                .maybeSingle();

            if (!linkRow) {
                setLink(null);
                setPayments([]);
                return;
            }
            setLink(linkRow as unknown as LinkRow);

            // RLS allows tenants to read all payments on their linked property,
            // but we also scope client-side to this tenant_link_id for clarity.
            const { data: pays, error } = await supabase
                .from('rent_payments')
                .select('id, property_id, tenant_link_id, amount, due_date, paid_date, status, period_month, days_late, notes')
                .eq('tenant_link_id', linkRow.id)
                .order('due_date', { ascending: false });

            if (error) {
                console.error('[TenantPaymentHistory] rent_payments error:', error);
                setPayments([]);
            } else {
                setPayments((pays || []) as RentPayment[]);
            }
        } catch (err) {
            console.error('[TenantPaymentHistory] error:', err);
        } finally {
            setLoading(false);
        }
    };

    // ── Derived stats ─────────────────────────────────────────────────────
    const stats = useMemo(() => {
        const paid = payments.filter(p => p.status === 'paid');
        const upcoming = payments.filter(p => p.status === 'pending' || p.status === 'overdue');
        const totalPaid = paid.reduce((s, p) => s + Number(p.amount || 0), 0);

        // This month paid
        const now = new Date();
        const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        const thisMonth = paid
            .filter(p => (p.paid_date || '').startsWith(ym))
            .reduce((s, p) => s + Number(p.amount || 0), 0);

        // Next due — earliest pending/overdue with a due_date in the future (or just earliest)
        const sortedUpcoming = [...upcoming].sort((a, b) => (a.due_date || '').localeCompare(b.due_date || ''));
        const nextDue = sortedUpcoming[0] || null;

        // On-time streak: count consecutive most-recent paid payments with days_late === 0
        const paidSortedDesc = [...paid].sort((a, b) => (b.paid_date || '').localeCompare(a.paid_date || ''));
        let streak = 0;
        for (const p of paidSortedDesc) {
            if ((p.days_late ?? 0) === 0) streak += 1;
            else break;
        }

        return { totalPaid, thisMonth, nextDue, streak, paidCount: paid.length, upcomingCount: upcoming.length };
    }, [payments]);

    const propertyName = link?.properties?.name || '';
    const rentAmount = link?.properties?.rent_amount ?? null;

    // ── Loading ───────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div className="page">
                <div className="page__header">
                    <div>
                        <h1 className="page__title">Payments</h1>
                        <p className="page__subtitle">Your payment history and upcoming invoices</p>
                    </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
                    <Loader2 className="animate-spin" size={40} color="var(--color-accent, #16a34a)" />
                </div>
            </div>
        );
    }

    // ── Not linked ────────────────────────────────────────────────────────
    if (!link) {
        return (
            <div className="page">
                <div className="page__header">
                    <div>
                        <h1 className="page__title">Payments</h1>
                        <p className="page__subtitle">Your payment history and upcoming invoices</p>
                    </div>
                </div>
                <div className="data-panel" style={{ padding: '4rem 2rem', textAlign: 'center' }}>
                    <CreditCard size={40} opacity={0.2} style={{ marginBottom: '0.75rem' }} />
                    <h3 style={{ margin: '0 0 0.5rem' }}>You're not linked to a property yet</h3>
                    <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
                        Enter your invite code on the Connect page to see your payment history.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="page">
            <div className="page__header">
                <div>
                    <h1 className="page__title">Payments</h1>
                    <p className="page__subtitle">
                        {propertyName ? `${propertyName} · ` : ''}Your payment history
                    </p>
                </div>
            </div>

            {paidFlag && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '12px', padding: '0.75rem 1rem', marginBottom: '1rem' }}>
                    <CheckCircle2 size={18} color="#16a34a" />
                    <div style={{ flex: 1, fontSize: '0.875rem', color: '#065f46', fontWeight: 600 }}>
                        Payment received — thanks! Your landlord has been notified.
                    </div>
                    <button onClick={dismissFlag} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#065f46', display: 'flex' }}><X size={14} /></button>
                </div>
            )}
            {canceledFlag && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px', padding: '0.75rem 1rem', marginBottom: '1rem' }}>
                    <AlertTriangle size={18} color="#dc2626" />
                    <div style={{ flex: 1, fontSize: '0.875rem', color: '#991b1b', fontWeight: 600 }}>
                        Payment canceled. Your rent is still unpaid — try again when ready.
                    </div>
                    <button onClick={dismissFlag} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#991b1b', display: 'flex' }}><X size={14} /></button>
                </div>
            )}

            {/* ── Hero summary ────────────────────────────────────────────── */}
            <div className="overview-grid" style={{ marginBottom: '1.5rem' }}>
                <div className="overview-card">
                    <div className="overview-card__header">
                        <span className="overview-card__title">Total Paid</span>
                        <div className="overview-card__icon" style={{ background: 'rgba(22,163,74,0.10)', color: '#16a34a' }}>
                            <CheckCircle2 size={18} />
                        </div>
                    </div>
                    <div className="overview-card__value">{formatCurrency(stats.totalPaid)}</div>
                    <div className="overview-card__label">{stats.paidCount} payment{stats.paidCount === 1 ? '' : 's'}</div>
                </div>

                <div className="overview-card">
                    <div className="overview-card__header">
                        <span className="overview-card__title">This Month</span>
                        <div className="overview-card__icon" style={{ background: 'rgba(37,99,235,0.10)', color: '#2563eb' }}>
                            <Calendar size={18} />
                        </div>
                    </div>
                    <div className="overview-card__value">{formatCurrency(stats.thisMonth)}</div>
                    <div className="overview-card__label">Paid this month</div>
                </div>

                <div className="overview-card">
                    <div className="overview-card__header">
                        <span className="overview-card__title">Next Due</span>
                        <div className="overview-card__icon" style={{ background: 'rgba(245,158,11,0.10)', color: '#d97706' }}>
                            <Clock size={18} />
                        </div>
                    </div>
                    <div className="overview-card__value">
                        {stats.nextDue ? formatCurrency(Number(stats.nextDue.amount)) : rentAmount != null ? formatCurrency(rentAmount) : '—'}
                    </div>
                    <div className="overview-card__label">
                        {stats.nextDue?.due_date ? `Due ${formatDate(stats.nextDue.due_date)}` : stats.upcomingCount === 0 ? 'All caught up' : 'Upcoming'}
                    </div>
                </div>

                <div className="overview-card">
                    <div className="overview-card__header">
                        <span className="overview-card__title">On-Time Streak</span>
                        <div className="overview-card__icon" style={{ background: 'rgba(239,68,68,0.10)', color: '#ef4444' }}>
                            <Flame size={18} />
                        </div>
                    </div>
                    <div className="overview-card__value">{stats.streak}</div>
                    <div className="overview-card__label">Consecutive on-time</div>
                </div>
            </div>

            {/* ── Payment history ─────────────────────────────────────────── */}
            <div className="data-panel">
                <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800 }}>Payment history</h3>
                    {rentAmount != null && (
                        <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                            Monthly rent: <strong style={{ color: 'var(--color-text)' }}>{formatCurrency(rentAmount)}</strong>
                        </span>
                    )}
                </div>

                {payments.length === 0 ? (
                    <div style={{ padding: '4rem 2rem', textAlign: 'center', color: 'var(--color-text-tertiary)' }}>
                        <CreditCard size={40} opacity={0.2} style={{ marginBottom: '0.75rem' }} />
                        <div style={{ fontWeight: 700, color: 'var(--color-text)', marginBottom: '0.25rem' }}>
                            No payments yet
                        </div>
                        <div style={{ fontSize: '0.875rem' }}>
                            Your payment history will appear here once your landlord records payments.
                        </div>
                    </div>
                ) : (
                    <div className="responsive-table">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Period</th>
                                    <th>Amount</th>
                                    <th>Due</th>
                                    <th>Paid</th>
                                    <th>Status</th>
                                    <th style={{ textAlign: 'right' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {payments.map(p => {
                                    const isPayable = (p.status === 'pending' || p.status === 'overdue') && landlord.stripeConnectChargesEnabled;
                                    return (
                                        <tr key={p.id}>
                                            <td style={{ fontWeight: 600 }}>
                                                {p.period_month ? formatDate(p.period_month) : '—'}
                                            </td>
                                            <td style={{ fontWeight: 700 }}>{formatCurrency(Number(p.amount))}</td>
                                            <td style={{ color: 'var(--color-text-secondary)' }}>
                                                {p.due_date ? formatDate(p.due_date) : '—'}
                                            </td>
                                            <td style={{ color: 'var(--color-text-secondary)' }}>
                                                {p.paid_date ? formatDate(p.paid_date) : '—'}
                                            </td>
                                            <td>
                                                {p.status === 'paid' && (
                                                    <span className="badge badge--green"><CheckCircle2 size={12} /> Paid</span>
                                                )}
                                                {p.status === 'pending' && (
                                                    <span className="badge badge--yellow"><Clock size={12} /> Pending</span>
                                                )}
                                                {p.status === 'overdue' && (
                                                    <span className="badge badge--red"><AlertTriangle size={12} /> Overdue</span>
                                                )}
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                {isPayable ? (
                                                    <button
                                                        onClick={() => handlePayNow(p)}
                                                        disabled={payingId === p.id}
                                                        style={{
                                                            display: 'inline-flex', alignItems: 'center', gap: 6,
                                                            padding: '0.375rem 0.875rem', borderRadius: 8,
                                                            border: 'none',
                                                            background: p.status === 'overdue'
                                                                ? 'linear-gradient(135deg, #ef4444, #b91c1c)'
                                                                : 'linear-gradient(135deg, #16a34a, #15803d)',
                                                            color: '#fff', fontWeight: 700, fontSize: '0.8125rem',
                                                            cursor: payingId === p.id ? 'wait' : 'pointer',
                                                            boxShadow: '0 2px 8px rgba(22,163,74,0.22)',
                                                        }}
                                                    >
                                                        {payingId === p.id
                                                            ? <><Loader2 size={12} className="animate-spin" /> Loading…</>
                                                            : <><Banknote size={12} /> Pay Now</>
                                                        }
                                                    </button>
                                                ) : (
                                                    <span style={{ color: 'var(--color-text-tertiary)', fontSize: '0.75rem' }}>—</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Hint */}
            <div style={{ marginTop: '1rem', fontSize: '0.75rem', color: 'var(--color-text-tertiary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <TrendingUp size={12} />
                {landlord.stripeConnectChargesEnabled
                    ? 'Pay rent online in seconds — secure card checkout powered by Stripe.'
                    : 'Your landlord hasn\'t enabled online payments yet. Pay via your usual method.'}
            </div>
        </div>
    );
}
